import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App, { StaffSignIn } from './App';
import { signInPending } from './staffAuth';
import AgentPortal from './AgentPortal';
import ResellerPortal from './ResellerPortal';
import Console from './Console';
import NewSale from './NewSale';
import reportWebVitals from './reportWebVitals';
import { stopFocusZoom } from './shared';
import { applyFirmTab } from './firmTab';

stopFocusZoom();

// Which surface this is.
//
// There is no router in this app and this does not introduce one — the path is
// read once, here, and decides which root renders. Firebase Hosting rewrites
// everything to /index.html, so /agent serves this same bundle.
//
// The staff portal stays on every other path, including `/`, so nothing about
// how staff reach it changes.
//
// There are three surfaces now. `/join` and `/admin` are the reseller's — a
// brand signing up, and the admin it lands in — and they are the same component
// because the difference between them is one boolean: which way round to offer
// signing up and signing in. A visitor who has a session sees the admin from
// either path.
//
// `/console` is a staff member's own account, their firm's team and — for a
// super admin — every firm. See `Console.js`.
//
// `/sell` is the public new sale page: an address in, a matter opened for
// staff to review. See `NewSale.js`. It is the brand refresh with the
// scroll-driven story; `/sellv2` (where it was tried, and where links already
// sent point) is the same page, and `/sellv1` keeps the earlier look.
const surface = (() => {
  try {
    const path = window.location.pathname;
    if (/^\/agent(\/|$)/i.test(path)) return 'agent';
    if (/^\/join(\/|$)/i.test(path)) return 'join';
    if (/^\/admin(\/|$)/i.test(path)) return 'admin';
    if (/^\/console(\/|$)/i.test(path)) return 'console';
    if (/^\/sell(\/|$)/i.test(path)) return 'sell';
    if (/^\/sellv2(\/|$)/i.test(path)) return 'sellv2';
    if (/^\/sellv1(\/|$)/i.test(path)) return 'sellv1';
    return 'staff';
  } catch (e) {
    return 'staff';
  }
})();

const ROOTS = {
  agent: <AgentPortal />,
  join: <ResellerPortal joining />,
  admin: <ResellerPortal />,
  console: <Console />,
  sell: <NewSale v2 />,
  sellv2: <NewSale v2 />,
  sellv1: <NewSale />,
  staff: <App />,
};

// The staff portal sets the tab once it knows the firm. The other surfaces
// are this deployment's own, and the static page deliberately names nobody.
if (surface !== 'staff') applyFirmTab(null);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    {/* A sign-in hand-off (`?signin=`) finishes before the staff portal
        loads, so it is redeemed even where the shared gate is open and
        nothing is ever refused. */}
    {surface === 'staff' && signInPending() ? <StaffSignIn /> : ROOTS[surface]}
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
